import { useParams } from '@tanstack/react-router'
import {
  Shield,
  Building2,
  BookOpen,
  UsersRound,
  FolderOpen,
  ChevronLeft,
  BarChart3,
  MessageCircle,
  NotebookPen,
  TriangleAlert,
} from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { CardGridSkeleton } from './CardGridSkeleton'
import { FileCardSkeleton } from './FileCardSkeleton'
import { FormCardSkeleton } from './FormCardSkeleton'
import { PageSkeleton } from './PageSkeleton'
import { TableCardSkeleton } from './TableCardSkeleton'
import { TaskDetailSkeleton } from './TaskDetailSkeleton'
import { GroupDetailFeedSkeleton, GroupInboxFeedSkeleton } from '@/components/groups/GroupFeedSkeletons'
import { BaseKanbanBoard } from '@/components/kanban/BaseKanbanBoard'
import { BlueprintStepSkeleton } from '@/components/kanban/BlueprintStepSkeleton'
import { founderColumns, teamColumns } from '@/components/kanban/kanban-columns'
import { PlaybookCardSkeleton } from '@/components/kanban/PlaybookCardSkeleton'
import { PageLayout } from '@/components/layout/PageLayout'
import { ChatSkeleton } from '@/components/messages/ChatSkeleton'
import { PlaybookDetailSkeleton } from '@/components/playbook/PlaybookDetailSkeleton'
import { BackButton } from '@/components/ui/back-button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useTasksTab } from '@/hooks/useTasksTab'

// ─── Shared subtitle constants ───────────────────────────────────

export const blueprintSubtitle = (
  <span style={{ maxWidth: 730, display: 'inline-block' }}>
    These are the 7 steps that every business needs to be both profitable and lifestyle friendly. This view
    shows which Doing Playbooks support each stage of the customer journey. Click on the Blueprint pillar, to
    show the associated playbooks.
  </span>
)

// ─── Helpers ─────────────────────────────────────────────────────

const noop = () => {}

function adminTitle(Icon: ComponentType<{ className?: string }>, label: string) {
  return (
    <span className="flex items-center gap-2">
      <Icon className="w-6 h-6 text-primary" />
      {label}
    </span>
  )
}

function makeTablePageSkeleton(title: ReactNode, subtitle: string): () => React.JSX.Element {
  return function TablePageSkeleton() {
    return (
      <PageSkeleton title={title} subtitle={subtitle}>
        <TableCardSkeleton />
      </PageSkeleton>
    )
  }
}

function PlaybookCardGrid() {
  return (
    <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
      {Array.from({ length: 12 }).map((_, i) => (
        <PlaybookCardSkeleton key={i} />
      ))}
    </div>
  )
}

function BlueprintStepsGrid() {
  return (
    <Card className="shadow-sm border-0" style={{ backgroundColor: 'hsl(var(--background) / 0.85)' }}>
      <CardContent className="p-4 sm:p-6 lg:p-10 @container">
        <div
          className="grid pb-4 gap-2 sm:gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))' }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <BlueprintStepSkeleton key={i} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

const skeletonItems = [{ key: 'sk-1' }, { key: 'sk-2' }]

function KanbanSkeleton() {
  return (
    <BaseKanbanBoard
      columns={founderColumns.map((col) => ({ ...col, items: skeletonItems }))}
      getItemKey={(item) => item.key}
      renderCard={() => <PlaybookCardSkeleton />}
    />
  )
}

// ─── Route pending components ────────────────────────────────────

export function DoerHomePending() {
  // Mirror the real DoerHome: founder cards row, a "This week at a glance"
  // heading, then the structured tile grid (Tasks 2-col list, AI Coaching +
  // Submit stacked, Messages 2-col, Start here). Tiles sit on white card
  // surfaces with the same radius/shadow as the live tiles.
  //
  // Gate each tile on the same flag DoerHome uses, so the skeleton's tile count
  // matches the page that replaces it — otherwise the grid reflows when the
  // skeleton swaps out. Loyalty carries its own flag, so it needs its own check
  // here: with only `coachingEnabled` the skeleton was a tile short whenever
  // points were on.
  const { hasFlag } = useFeatureFlags()
  const coachingEnabled = hasFlag('pre-session-prep')
  const loyaltyEnabled = hasFlag('loyalty-points')
  const tileShadow = 'shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_rgba(16,24,40,0.05)]'
  const tileClass = `rounded-[20px] border border-border/70 bg-card p-[22px_24px] ${tileShadow}`
  const founderCardClass = `rounded-[20px] border border-border/70 bg-card p-5 ${tileShadow}`

  function TileHeader({ withAction = false }: { withAction?: boolean }) {
    return (
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-[38px] w-[38px] shrink-0 rounded-xl" />
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-28 rounded" />
            <Skeleton className="h-3 w-20 rounded" />
          </div>
        </div>
        {withAction && <Skeleton className="h-4 w-14 rounded" />}
      </div>
    )
  }

  function RowSkeleton() {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-border/70 p-[12px_14px]">
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4 rounded" />
          <Skeleton className="h-3 w-24 rounded" />
        </div>
        <Skeleton className="h-4 w-4 shrink-0 rounded" />
      </div>
    )
  }

  return (
    <PageSkeleton title="Welcome back" subtitle="DOER DASHBOARD">
      {/* Founder relationship cards */}
      <div className="mb-8 space-y-4">
        <Skeleton className="h-5 w-32 rounded" />
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className={founderCardClass}>
              <div className="flex items-center gap-3">
                <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-28 rounded" />
                  <Skeleton className="h-3 w-20 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* This week at a glance — tile grid */}
      <Skeleton className="mb-4 h-5 w-44 rounded" />
      <div className="grid gap-[18px] lg:grid-cols-3">
        {/* Tasks — spans 2 cols on desktop */}
        <div className={`${tileClass} lg:col-span-2`}>
          <TileHeader withAction />
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <RowSkeleton key={i} />
            ))}
            <Skeleton className="mt-2 h-4 w-32 rounded" />
          </div>
        </div>

        {/* AI Coaching + Submit — only when the coaching loop is enabled */}
        {coachingEnabled && (
          <>
            <div className={tileClass}>
              <TileHeader />
              <Skeleton className="h-11 w-full rounded-2xl" />
              <Skeleton className="mt-3 h-4 w-full rounded" />
              <Skeleton className="mt-2 h-4 w-2/3 rounded" />
              <Skeleton className="mt-4 h-4 w-40 rounded" />
            </div>

            <div className={tileClass}>
              <TileHeader />
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="mt-2 h-4 w-5/6 rounded" />
              <Skeleton className="mt-4 h-10 w-full rounded-lg" />
            </div>
          </>
        )}

        {/* Loyalty — balance figure over its most recent earning */}
        {loyaltyEnabled && (
          <div className={tileClass}>
            <TileHeader withAction />
            <Skeleton className="h-10 w-24 rounded" />
            <Skeleton className="mt-3 h-4 w-44 rounded" />
          </div>
        )}

        {/* Messages */}
        <div className={tileClass}>
          <TileHeader withAction />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3 rounded" />
                  <Skeleton className="h-3 w-3/5 rounded" />
                </div>
                <Skeleton className="h-3 w-8 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Start here */}
        <div className={tileClass}>
          <TileHeader />
          <div className="space-y-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-2">
                <Skeleton className="h-[34px] w-[34px] shrink-0 rounded-[10px]" />
                <Skeleton className="h-4 flex-1 rounded" />
                <Skeleton className="h-4 w-4 shrink-0 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageSkeleton>
  )
}

export function StartHerePending() {
  return (
    <PageSkeleton title="Start Here" subtitle="WELCOME">
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Skeleton className="w-full h-[400px] rounded-[20px]" />
        <Skeleton className="w-48 h-8 mx-auto mt-12 rounded-lg" />
        <div className="grid grid-cols-3 gap-6 mt-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="w-full h-48 rounded-xl" />
          ))}
        </div>
      </div>
    </PageSkeleton>
  )
}

export function DoingBlueprintPending() {
  return (
    <PageSkeleton
      title="Doing Blueprint"
      subtitle={blueprintSubtitle}
      search={{
        value: '',
        onChange: noop,
        placeholder: 'Search Playbooks',
        inputClassName: 'bg-surface-soft',
        className: 'max-w-xs',
      }}
    >
      <BlueprintStepsGrid />
      <div style={{ marginTop: 16 }}>
        <PlaybookCardGrid />
      </div>
    </PageSkeleton>
  )
}

export function PlaybooksPending() {
  return (
    <PageSkeleton
      title="Doing Playbooks"
      subtitle={
        <>
          <div>
            Here is a project management view for all of the Doing Playbooks included in your Founder Roadmap.
            Below you can see the status of each project.
          </div>
          <div>To add additional Playbooks, click + Add a Playbook</div>
        </>
      }
      search={{
        value: '',
        onChange: noop,
        placeholder: 'Search playbooks...',
        inputClassName: 'bg-surface-soft',
      }}
    >
      <KanbanSkeleton />
    </PageSkeleton>
  )
}

export function PlaybookDetailPending() {
  const { slug } = useParams({ strict: false }) as { slug?: string }

  return (
    <PageSkeleton
      title={<Skeleton className="h-7 w-56 rounded" />}
      subtitle={<Skeleton className="h-4 w-32 rounded" />}
      actions={
        <span className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.13] pl-3 pr-3.5 py-1.5 text-sm font-medium text-black">
          <ChevronLeft className="w-5 h-5" />
          Return to Doing Teams
        </span>
      }
    >
      <PlaybookDetailSkeleton slug={slug} />
    </PageSkeleton>
  )
}

export function BusinessBriefPending() {
  return (
    <PageSkeleton title="Business Brief" subtitle="Your business profile">
      <div className="rounded-xl border bg-card text-card-foreground shadow">
        <div className="flex flex-col space-y-1.5 p-6">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="p-6 pt-0 space-y-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-5 w-full max-w-md" />
            </div>
          ))}
        </div>
      </div>
    </PageSkeleton>
  )
}

export function MessagesPending() {
  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* Sidebar skeleton */}
      <div className="w-[432px] shrink-0 flex flex-col bg-white border-r border-surface-border px-5 pt-10">
        <Skeleton className="h-9 w-48 mb-5 rounded-md" />
        <Skeleton className="h-10 w-full rounded-full mb-5" />
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-1">
              <Skeleton className="w-[34px] h-[34px] rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4 rounded" />
                <Skeleton className="h-3 w-1/2 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Chat area skeleton */}
      <div className="flex-1 p-5 bg-surface-muted">
        <div className="bg-white rounded-[10px] shadow-message-panel h-full overflow-hidden">
          <ChatSkeleton />
        </div>
      </div>
    </div>
  )
}

function TeamKanbanSkeleton() {
  return (
    <BaseKanbanBoard
      columns={teamColumns.map((col) => ({ ...col, items: skeletonItems }))}
      getItemKey={(item) => item.key}
      renderCard={() => <PlaybookCardSkeleton />}
    />
  )
}

export function TeamsPending() {
  return (
    <PageSkeleton
      title="Doing Teams"
      subtitle="Playbooks assigned to a Doing Team"
      search={{
        value: '',
        onChange: noop,
        placeholder: 'Search playbooks...',
        inputClassName: 'bg-surface-soft',
      }}
    >
      <TeamKanbanSkeleton />
    </PageSkeleton>
  )
}

export function SettingsPending() {
  return (
    <PageSkeleton title="Settings" subtitle="Your account details">
      <FormCardSkeleton />
    </PageSkeleton>
  )
}

// Its own skeleton rather than SettingsPending's: /settings/activity-agent is
// parented on the settings layout but is not the Settings page, and borrowing
// that skeleton meant the doer watched a "Settings / Your account details"
// header resolve into "Activity Agent".
export function ActivityAgentPending() {
  return (
    <PageSkeleton title="Activity Agent" subtitle="Download the latest desktop agent build">
      <FormCardSkeleton fields={2} />
    </PageSkeleton>
  )
}

export function MyActivityPending() {
  return (
    <PageSkeleton title="My Activity" subtitle="What the Activity Agent is recording on your devices">
      <FormCardSkeleton fields={2} />
    </PageSkeleton>
  )
}

export function MyTeamProjectsPending() {
  return (
    <PageSkeleton
      title={
        <span className="flex items-center gap-2">
          <FolderOpen className="w-6 h-6 text-primary" />
          Projects
        </span>
      }
      subtitle="View and manage projects assigned to you"
    >
      <CardGridSkeleton />
    </PageSkeleton>
  )
}

export function AdminUsersPending() {
  return (
    <PageSkeleton
      title={adminTitle(Shield, 'Admin Panel')}
      subtitle="Manage users, roles, and workspace Doer assignments"
      search={{
        value: '',
        onChange: noop,
        placeholder: 'Search users or playbooks...',
      }}
    >
      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-2xl p-4 space-y-2 shadow-[48px_27px_74px_0px_rgba(8,21,52,0.12)]"
          >
            <Skeleton className="h-3 w-20 rounded" />
            <Skeleton className="h-7 w-16 rounded" />
          </div>
        ))}
      </div>
      <TableCardSkeleton />
    </PageSkeleton>
  )
}

export function AdminStatsPending() {
  return (
    <PageSkeleton title={adminTitle(BarChart3, 'Stats')} subtitle="Loading platform-wide admin metrics">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index} className="shadow-sm">
            <CardContent className="space-y-3 p-5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-4 w-40" />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-12">
        {[
          'xl:col-span-8',
          'xl:col-span-4',
          'xl:col-span-4',
          'xl:col-span-4',
          'xl:col-span-4',
          'xl:col-span-5',
          'xl:col-span-3',
          'xl:col-span-4',
          'xl:col-span-6',
          'xl:col-span-6',
        ].map((className, index) => (
          <Card key={index} className={className}>
            <CardContent className="space-y-3 p-5">
              <Skeleton className="h-5 w-36" />
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-[220px] w-full rounded-lg" />
            </CardContent>
          </Card>
        ))}
      </div>
    </PageSkeleton>
  )
}

export function NotificationsPending() {
  return (
    <PageSkeleton title="Notifications">
      <Card className="shadow-sm border-0 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="flex border-b border-border">
            <div className="flex-1 flex items-center gap-2 h-[51px] px-5">
              <Skeleton className="h-[18px] w-[34px] rounded-full" />
              <Skeleton className="h-4 w-6 rounded" />
            </div>
            <div className="flex-1 flex items-center gap-2 h-[51px] px-5">
              <Skeleton className="h-[18px] w-[34px] rounded-full" />
              <Skeleton className="h-4 w-10 rounded" />
            </div>
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-[17px] h-[68px] px-[19px] py-[5px]">
              <Skeleton className="w-[10px] h-[10px] rounded-full shrink-0" />
              <Skeleton className="w-[58px] h-[58px] rounded-full shrink-0" />
              <Skeleton className="h-[14px] flex-1 rounded" />
              <Skeleton className="h-[14px] w-[30px] rounded" />
            </div>
          ))}
        </CardContent>
      </Card>
    </PageSkeleton>
  )
}

export function FilesPending() {
  return (
    <PageSkeleton title="Files" subtitle="Manage your project files and folders">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-xl border border-border bg-background p-4 shadow-sm"
          >
            <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-24 rounded" />
              <Skeleton className="h-3 w-14 rounded" />
            </div>
          </div>
        ))}
      </div>
      <FileCardSkeleton />
    </PageSkeleton>
  )
}

/**
 * The board, loading — in the shape of the tab being arrived at.
 *
 * The tab comes off the matched route's `staticData` (see `useTasksTab`), which
 * is available here because the router commits the pending matches before it
 * paints their pending component. One table skeleton on all four tabs was a
 * promise the Kanban and Calendar tabs then broke.
 *
 * A tab it cannot name gets the plain block, never the List table: the whole
 * point of loading in the tab's shape is that the founder never sees a frame of
 * a view they are not going to.
 */
export function TasksPending() {
  const tab = useTasksTab()

  return (
    <PageSkeleton title="Tasks" subtitle="Every task across every playbook. One board.">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-10 w-64 rounded-md" />
        <Skeleton className="h-6 w-36 rounded-md" />
      </div>
      {tab === 'list' ? (
        <TableCardSkeleton />
      ) : tab === 'kanban' ? (
        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[420px] rounded-2xl" />
          ))}
        </div>
      ) : tab === 'collections' ? (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-2xl" />
          ))}
        </div>
      ) : (
        <Skeleton className="h-[560px] rounded-2xl" />
      )}
    </PageSkeleton>
  )
}

/**
 * One collection, loading. Its own skeleton rather than the board's: this page
 * is titled after the collection and backed by a "Collections" button, so
 * painting "Tasks — Every task across every playbook" (with the board's search
 * field) named the wrong page on the way in.
 */
export function TaskCollectionDetailPending() {
  return (
    <PageSkeleton
      title={<Skeleton className="h-7 w-56 rounded" />}
      subtitle="Every task in this collection, sorted by project."
      actions={<BackButton to="/tasks/collections">Collections</BackButton>}
    >
      <TableCardSkeleton />
    </PageSkeleton>
  )
}

export function TaskCreatePending() {
  return (
    <PageSkeleton
      title="Create task"
      subtitle="Assign it, schedule it, and it lands on the board."
      actions={<BackButton to="/tasks">All Tasks</BackButton>}
    >
      <div className="mx-auto w-full min-w-0 max-w-[760px]">
        <FormCardSkeleton fields={6} className="max-w-none" />
      </div>
    </PageSkeleton>
  )
}

export function TaskDetailPending() {
  return (
    <PageSkeleton
      title={<Skeleton className="h-7 w-48 rounded" />}
      subtitle={<Skeleton className="h-4 w-24 rounded" />}
      actions={<BackButton to="/tasks">All Tasks</BackButton>}
    >
      <TaskDetailSkeleton />
    </PageSkeleton>
  )
}

// Group routes render inside the shared GroupsLayout, which owns the sidebar
// and column grid. Their pending UI is feed-only so the shell stays painted
// (no jump). These reuse the exact skeletons the pages use when loading.
export function GroupsPending() {
  return <GroupInboxFeedSkeleton />
}

export function GroupInboxPending() {
  return <GroupInboxFeedSkeleton />
}

export function KickOffCallPending() {
  return (
    <PageSkeleton title="Kick-Off Call">
      <div className="flex flex-col gap-8 w-full">
        <Skeleton className="h-[312px] w-full rounded-2xl" />
        <Skeleton className="h-[60px] w-full max-w-md mx-auto rounded" />
        <Skeleton className="h-[526px] w-full rounded-lg" />
        <Skeleton className="h-[447px] w-full rounded-2xl" />
        <div className="grid grid-cols-3 gap-6 pt-6">
          <Skeleton className="h-[283px] rounded-3xl" />
          <Skeleton className="h-[283px] rounded-3xl" />
          <Skeleton className="h-[283px] rounded-3xl" />
        </div>
      </div>
    </PageSkeleton>
  )
}

export function DoersUniversityPending() {
  // The real page stacks centered 974px sections: a purple-gradient hero, a white
  // "six series" card, a white/blue-gradient "how it works" panel, and a white
  // certifications card. Skeleton blocks must sit on those coloured/white
  // surfaces — bare `bg-muted` blocks on the grey page are invisible.
  return (
    <PageSkeleton title="Doer's University">
      <div className="flex w-full flex-col gap-9">
        {/* Hero — purple gradient with copy + video */}
        <div
          className="flex min-h-[284px] w-full items-center justify-between gap-[55px] rounded-2xl p-[35px_55px] shadow-sm max-lg:flex-col max-lg:text-center"
          style={{ background: 'linear-gradient(180deg, #541af1 23%, #300f8b 180%)' }}
        >
          <div className="w-[422px] max-w-full space-y-4">
            <Skeleton className="h-8 w-4/5 rounded bg-white/25" />
            <Skeleton className="h-4 w-full rounded bg-white/25" />
            <Skeleton className="h-4 w-2/3 rounded bg-white/25" />
            <div className="flex gap-2.5 pt-2 max-lg:justify-center">
              <Skeleton className="h-[45px] w-[186px] rounded-md bg-white/25" />
              <Skeleton className="h-[45px] w-[186px] rounded-md bg-white/25" />
            </div>
          </div>
          <Skeleton className="aspect-[378/213] w-[378px] max-w-[45%] shrink-0 rounded-lg bg-white/25 max-lg:max-w-full" />
        </div>

        {/* Six series — white card with header + 3-col grid */}
        <div className="flex w-full flex-col items-center gap-[50px] rounded-2xl bg-card p-[50px_36px_36px] shadow-sm">
          <div className="flex flex-col items-center gap-[22px]">
            <Skeleton className="h-7 w-[600px] max-w-full rounded" />
            <Skeleton className="h-[45px] w-80 max-w-full rounded-md" />
          </div>
          <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-x-[15px] gap-y-8 pt-[26px]">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-6 rounded-[25px] border border-primary/30 p-[42px_17px_24px]">
                <Skeleton className="h-6 w-2/3 rounded" />
                <Skeleton className="h-4 w-full rounded" />
                <Skeleton className="h-4 w-5/6 rounded" />
                <Skeleton className="h-8 w-[132px] rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* How it works — white/blue gradient panel with steps */}
        <div
          className="flex w-full flex-col items-center gap-9 rounded-2xl p-[39px_55px_34px] shadow-sm"
          style={{ background: 'linear-gradient(180deg, #fff 0%, #ced5ff 149%)' }}
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <Skeleton className="h-7 w-56 rounded" />
            <Skeleton className="h-4 w-[700px] max-w-full rounded" />
            <Skeleton className="h-4 w-3/4 rounded" />
          </div>
          <div className="grid w-full max-w-[1100px] grid-cols-4 gap-[30px] max-lg:grid-cols-2 max-[640px]:grid-cols-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-4 text-center">
                <Skeleton className="h-9 w-9 rounded" />
                <Skeleton className="h-5 w-2/3 rounded" />
                <Skeleton className="h-3 w-full rounded" />
                <Skeleton className="h-3 w-5/6 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Certifications — white card with header, tabs + badge grid */}
        <div className="flex w-full flex-col items-center gap-9 rounded-2xl bg-card p-[35px_55px_36px] shadow-sm">
          <div className="flex w-full flex-col items-start gap-6">
            <div className="space-y-2.5">
              <Skeleton className="h-7 w-96 max-w-full rounded" />
              <Skeleton className="h-4 w-80 max-w-full rounded" />
            </div>
            <Skeleton className="h-[39px] w-[322px] max-w-full rounded-2xl" />
          </div>
          <div className="flex w-full flex-wrap gap-[15px]">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[34px] w-28 rounded" />
            ))}
          </div>
          <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-x-[18px] gap-y-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3.5">
                <Skeleton className="aspect-[265/148] w-full rounded-2xl" />
                <div className="space-y-2 px-[15px] pb-[13px]">
                  <Skeleton className="h-4 w-3/4 rounded" />
                  <Skeleton className="h-3 w-full rounded" />
                  <Skeleton className="h-3 w-5/6 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageSkeleton>
  )
}

export function MasterclassVaultPending() {
  return (
    <PageSkeleton title="Masterclass Vault" subtitle="Loading masterclasses…">
      <div className="rounded-2xl bg-card px-[57px] pb-[50px] pt-[35px] shadow-sm">
        <Skeleton className="mb-2 h-7 w-80 rounded" />
        <Skeleton className="mb-6 h-4 w-2/3 rounded" />
        <Skeleton className="mb-12 h-[39px] w-[322px] rounded-2xl" />
        <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-x-[18px] gap-y-12">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="aspect-[265/149] w-full rounded-2xl" />
              <Skeleton className="h-3 w-20 rounded" />
              <Skeleton className="h-4 w-3/4 rounded" />
              <Skeleton className="h-3 w-full rounded" />
              <Skeleton className="h-6 w-full rounded-full" />
            </div>
          ))}
        </div>
      </div>
    </PageSkeleton>
  )
}

// The real lesson pages (VideoLessonLayout) render an edge-to-edge white
// content surface: a 178px nav column with a purple progress band, a gradient
// hero, and a centered 509×286 video. Mirror that shell so the skeleton sits
// on white/gradient surfaces (not a flat grey void) and matches the layout.
// The Masterclass Vault's watch page only, since Founder Foundations moved to a
// card grid — still parameterised by title and back link, because that is the
// whole of what a second caller would change.
function VideoLessonPending({ title, back }: { title: string; back: { to: string; label: string } }) {
  return (
    <PageLayout
      title={title}
      actions={<BackButton to={back.to}>{back.label}</BackButton>}
      fullHeight
      contentClassName="p-0 lg:p-0 bg-card"
    >
      <div role="status" aria-label="Loading lesson" className="flex flex-1 min-h-0">
        {/* Fixed-width nav column — purple band + lesson list */}
        <aside className="hidden w-[178px] shrink-0 flex-col bg-card lg:flex">
          <div className="h-[74px] bg-primary" />
          <div className="flex flex-col gap-3 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 shrink-0 rounded" />
                <Skeleton className="h-4 w-full rounded" />
              </div>
            ))}
          </div>
        </aside>

        {/* Content: gradient hero + centered video */}
        <div className="flex flex-1 flex-col bg-card">
          <div
            className="flex flex-col items-center gap-4 px-10 py-12 text-center"
            style={{ background: 'linear-gradient(90deg, #dcdbff 0%, #fefdff 100%)' }}
          >
            <Skeleton className="h-3 w-32 rounded" />
            <Skeleton className="h-8 w-2/3 max-w-[620px] rounded" />
            <Skeleton className="h-5 w-5 rounded-full" />
          </div>
          <Skeleton className="mx-auto my-10 aspect-[508.759/286] w-full max-w-[509px] rounded-none" />
          <div className="mx-auto w-full max-w-[509px] space-y-3 px-10 pb-10">
            <Skeleton className="h-4 w-full rounded" />
            <Skeleton className="h-4 w-5/6 rounded" />
            <Skeleton className="h-4 w-3/4 rounded" />
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export function MasterclassLessonPending() {
  return (
    <VideoLessonPending
      title="Masterclass Vault"
      back={{ to: '/masterclass-vault', label: 'Back to Masterclasses' }}
    />
  )
}

export function FoundationsPending() {
  // The course is a grid of video cards on the grey page — each its own white
  // card, so the skeleton blocks sit on white rather than disappearing into the
  // background.
  return (
    <PageSkeleton title="Founder Foundations" subtitle="Loading your videos…">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-x-[23px] gap-y-[25px]">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-[10px] rounded-2xl bg-card p-4 shadow-sm">
            <Skeleton className="h-6 w-20 rounded-xl" />
            <Skeleton className="h-[18px] w-3/4 rounded" />
            <Skeleton className="aspect-[277/143] w-full rounded-[9.43px]" />
          </div>
        ))}
      </div>
    </PageSkeleton>
  )
}

export function FoundersLoungePending() {
  // The real page renders white card surfaces (hero, action cards, answer panel,
  // search panel) on the grey dashboard background. Skeleton blocks must sit on
  // those white surfaces — bare `bg-muted` blocks on the grey page are invisible.
  return (
    <PageSkeleton title="Founder's Lounge">
      <div className="space-y-12">
        {/* Hero */}
        <div className="flex min-h-[260px] items-center justify-between gap-8 rounded-[20px] bg-card p-10 shadow-sm">
          <div className="flex-1 space-y-4">
            <Skeleton className="h-4 w-28 rounded" />
            <Skeleton className="h-9 w-4/5 rounded" />
            <Skeleton className="h-4 w-full max-w-md rounded" />
            <Skeleton className="h-11 w-44 rounded-md" />
          </div>
          <Skeleton className="hidden h-[190px] w-[220px] shrink-0 rounded-2xl md:block" />
        </div>

        {/* What do you want to do? — action cards */}
        <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-4 rounded-2xl bg-card p-6 shadow-sm">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <Skeleton className="h-5 w-2/3 rounded" />
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-24 rounded" />
            </div>
          ))}
        </div>

        {/* We've got your answer — answer panel */}
        <div className="space-y-8 rounded-[20px] bg-card p-10 shadow-sm">
          <Skeleton className="h-6 w-56 rounded" />
          <div className="grid gap-8 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-5 w-2/3 rounded" />
                {Array.from({ length: 3 }).map((_, j) => (
                  <Skeleton key={j} className="h-4 w-full rounded" />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Search answers below — search panel */}
        <div className="space-y-6 rounded-[20px] bg-card p-10 shadow-sm">
          <Skeleton className="mx-auto h-6 w-44 rounded" />
          <Skeleton className="h-12 w-full rounded-full" />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-24 rounded-full" />
            ))}
          </div>
          <div className="grid gap-x-5 gap-y-9 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[265/139] w-full rounded-2xl" />
                <Skeleton className="h-4 w-3/4 rounded" />
                <Skeleton className="h-3 w-full rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageSkeleton>
  )
}

export function TechStackFinderPending() {
  // The real page renders three white card surfaces (hero, "this is for you",
  // cost) plus a bare closing CTA on the grey dashboard background. Skeleton
  // blocks must sit on those white surfaces — bare `bg-muted` blocks on the grey
  // page are invisible. The closing section is intentionally card-less to mirror
  // the real bare <section>.
  return (
    <PageSkeleton title="Tech Stack Finder">
      <div className="flex w-full flex-col items-center gap-9">
        {/* Hero — centered heading, copy, CTA */}
        <div className="flex min-h-[248px] w-full flex-col items-center justify-center gap-6 rounded-2xl bg-card p-10 shadow-sm">
          <div className="flex w-full max-w-[585px] flex-col items-center gap-4">
            <Skeleton className="h-8 w-4/5 rounded" />
            <Skeleton className="h-4 w-full rounded" />
            <Skeleton className="h-4 w-3/4 rounded" />
          </div>
          <Skeleton className="h-[45px] w-56 rounded-md" />
        </div>

        {/* This is for you if… — checklist + mockup */}
        <div className="flex min-h-[361px] w-full flex-col items-center justify-center gap-10 rounded-2xl bg-card p-12 lg:flex-row">
          <div className="flex w-full flex-col gap-4 lg:w-[460px]">
            <Skeleton className="h-7 w-2/3 rounded" />
            <Skeleton className="h-4 w-full max-w-[406px] rounded" />
            <div className="flex flex-col gap-2 pt-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Skeleton className="h-[15px] w-[15px] shrink-0 rounded-full" />
                  <Skeleton className="h-3.5 w-3/4 rounded" />
                </div>
              ))}
            </div>
          </div>
          <Skeleton className="h-[209px] w-full shrink-0 rounded-xl lg:w-[325px]" />
        </div>

        {/* The wrong tech stack… — photo collage + copy + CTA */}
        <div className="flex min-h-[421px] w-full flex-col-reverse items-center justify-center gap-14 rounded-2xl bg-card p-10 lg:flex-row">
          <Skeleton className="h-[357px] w-full shrink-0 rounded-2xl lg:w-[369px]" />
          <div className="flex w-full flex-col gap-6 lg:w-[411px]">
            <div className="flex flex-col gap-4">
              <Skeleton className="h-7 w-4/5 rounded" />
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-2/3 rounded" />
            </div>
            <Skeleton className="h-[45px] w-56 rounded-md" />
          </div>
        </div>

        {/* Closing CTA — bare on the page, matching the real <section> */}
        <div className="flex w-full max-w-[565px] flex-col items-center gap-5">
          <Skeleton className="h-7 w-full max-w-md rounded" />
          <Skeleton className="h-4 w-3/4 rounded" />
          <Skeleton className="h-[45px] w-56 rounded-md" />
        </div>
      </div>
    </PageSkeleton>
  )
}

export function FoundersLoungeArticlePending() {
  // The real article renders inside a white card (`.wrap` → hsl(var(--card))) on
  // the grey dashboard background, so the skeleton sits on a white surface too.
  return (
    <PageSkeleton title="Founder's Lounge">
      <div className="mx-auto max-w-[840px] space-y-6 rounded-[20px] bg-card p-10 shadow-sm">
        <Skeleton className="h-4 w-32 rounded" />
        <Skeleton className="aspect-[16/7] w-full rounded-2xl" />
        <Skeleton className="h-8 w-2/3 rounded" />
        <Skeleton className="h-4 w-full rounded" />
        <div className="space-y-3 pt-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-full rounded" />
          ))}
        </div>
      </div>
    </PageSkeleton>
  )
}

export function CoachingCallReplayPending() {
  return (
    <PageSkeleton title="Coaching Call Replays">
      <div className="grid gap-7 grid-cols-[260px_1fr]">
        <aside className="space-y-3 p-5 rounded-[18px]" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-5 w-full rounded" />
          ))}
        </aside>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-10">
            <Skeleton className="h-3 w-24 rounded mx-auto mb-2" />
            <Skeleton className="h-8 w-72 rounded mx-auto mb-6" />
            <Skeleton className="aspect-video w-full rounded-xl mb-6" />
            <div className="space-y-3">
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-3/4 rounded" />
              <Skeleton className="h-4 w-5/6 rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    </PageSkeleton>
  )
}

export function CoachingCallsPending() {
  return (
    <PageSkeleton title="Coaching Calls" subtitle="Browse upcoming 2-hour Q&A coaching sessions.">
      <Card>
        <CardContent className="flex flex-col gap-6 p-9">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-72 rounded" />
            <div className="flex gap-3">
              <Skeleton className="h-11 w-44 rounded-md" />
              <Skeleton className="h-11 w-32 rounded-md" />
            </div>
          </div>
          <div className="grid grid-cols-5 grid-rows-5 gap-2.5">
            {Array.from({ length: 25 }).map((_, i) => (
              <Skeleton key={i} className="h-[118px] w-full rounded-xl" />
            ))}
          </div>
        </CardContent>
      </Card>
    </PageSkeleton>
  )
}

export function GroupDetailPending() {
  return <GroupDetailFeedSkeleton />
}

export const AdminCategoriesPending = makeTablePageSkeleton(
  adminTitle(Building2, 'Categories'),
  'Manage founder categories',
)
export const AdminPlaybooksPending = makeTablePageSkeleton(
  adminTitle(BookOpen, 'Playbooks'),
  'Manage master playbooks',
)
export const AdminTeamsPending = makeTablePageSkeleton(adminTitle(UsersRound, 'Teams'), 'Manage doing teams')
export const AdminDoerGroupsPending = makeTablePageSkeleton(
  adminTitle(UsersRound, 'Doer Groups'),
  'Escalation pods and DSL teams',
)
export const AdminDoerGroupDetailPending = makeTablePageSkeleton('Doer Group', 'Loading group details...')
export const AdminGroupChatsPending = makeTablePageSkeleton(
  adminTitle(MessageCircle, 'Group Chats'),
  'Named chats between doers, provisioned by admins',
)
export const AdminGroupChatDetailPending = makeTablePageSkeleton(
  'Group Chat',
  'Loading group chat details...',
)
export const AdminCoachingSessionsPending = makeTablePageSkeleton(
  adminTitle(NotebookPen, 'Coaching Sessions'),
  'DSL weekly coaching sessions — internal dev + ops visibility',
)
export const AdminEscalationsPending = makeTablePageSkeleton(
  adminTitle(TriangleAlert, 'Escalations'),
  'The escalation-officer queue — claim, work, and resolve attrition signals',
)
export const AdminTeamDetailPending = makeTablePageSkeleton('Team', 'Loading team details...')
export const AdminCategoryDetailPending = makeTablePageSkeleton('Category', 'Loading category details...')
